// src/pages/Logs.jsx

import React, { useState, useEffect } from 'react';
import { useWabaAccounts, withAccount } from '../lib/waba';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Box,
  Button,
  Container,
  createTheme,
  ThemeProvider,
  useMediaQuery,
  Stack,
  Chip,
  Grid,
  Paper,
  Typography
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { Download, Refresh } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const theme = createTheme({
  palette: {
    primary:   { main: '#F96168' },
    secondary: { main: '#000000' },
    background: {
      default: '#ffffff',
      paper:   '#f8fafc'   // <— added so bgcolor="background.paper" resolves correctly
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '8px',
          transition: 'all 0.2s ease',
          letterSpacing: 0.5,
          height: 40,
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
        }
      }
    }
  }
});

const chartOptions = title => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title: {
      display: true,
      text: title,
      font: { size: 16, weight: 600 },
      color: '#0f172a'
    }
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: '#64748b', autoSkip: true, maxRotation: 45, minRotation: 45, maxTicksLimit: 8 }
    },
    y: {
      beginAtZero: true,
      grid: { color: '#f1f5f9' },
      ticks: { color: '#64748b' }
    }
  }
});

const chartDataConfig = (labels, data, color) => ({
  labels,
  datasets: [{
    label: '',
    data,
    borderColor: color,
    backgroundColor: `${color}33`,
    tension: 0.3,
    borderWidth: 2,
    pointRadius: 4,
    pointHoverRadius: 6,
    pointBackgroundColor: color
  }]
});

export default function Logs() {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [startDate, setStartDate] = useState(dayjs().subtract(7, 'day'));
  const [endDate,   setEndDate]   = useState(dayjs());
  const [data,      setData]      = useState([]);
  const [trends,    setTrends]    = useState(null);
  const [summary,   setSummary]   = useState(null);
  // Which account's log this is. Every query below carries it; a switch in
  // the header clears the table so a stale account's rows are never shown
  // under the new one's name.
  const { selected: wabaAccount } = useWabaAccounts();
  useEffect(() => { setData([]); setSummary(null); setTrends(null); }, [wabaAccount]);

  const columns = [
    { accessorKey: 'message_id',     header: 'Message ID' },
    { accessorKey: 'phone_number',   header: 'Phone Number' },
    { accessorKey: 'template_id',    header: 'Template' },
    {
      accessorKey: 'variable_params',
      header: 'Variables',
      Cell: ({ cell }) => JSON.stringify(cell.getValue())
    },
    {
      accessorKey: 'sent_time',
      header: 'Sent Time',
      Cell: ({ cell }) => dayjs(cell.getValue()).format('DD/MM/YY HH:mm')
    },
    {
      accessorKey: 'delivered_time',
      header: 'Delivered',
      Cell: ({ cell }) =>
        cell.getValue() ? dayjs(cell.getValue()).format('DD/MM/YY HH:mm') : '-'
    },
    {
      accessorKey: 'read_time',
      header: 'Read',
      Cell: ({ cell }) =>
        cell.getValue() ? dayjs(cell.getValue()).format('DD/MM/YY HH:mm') : '-'
    },
    { accessorKey: 'retries', header: 'Retries' }
  ];

  const handleFetch = async () => {
    const s = startDate.format('YYYY-MM-DD');
    const e = endDate.format('YYYY-MM-DD');
    try {
      const respLogs = await fetch(`/api${withAccount(`/logs?start=${s}&end=${e}`, wabaAccount)}`);
      if (!respLogs.ok) throw new Error(`Logs: ${respLogs.status}`);
      setData(await respLogs.json());

      const respTrend = await fetch(`/api${withAccount(`/analytics/message_trends?start=${s}&end=${e}`, wabaAccount)}`);
      if (!respTrend.ok) throw new Error(`Trends: ${respTrend.status}`);
      const trendJson = await respTrend.json();
      setTrends({
        labels:            trendJson.map(t => t.date),
        deliveredRead:     trendJson.map(t => t.delivered_and_read),
        deliveredNotRead:  trendJson.map(t => t.delivered_not_read),
        sentNotDelivered:  trendJson.map(t => t.sent_not_delivered),
        failed:            trendJson.map(t => t.failed)
      });

      const respSummary = await fetch(`/api${withAccount(`/analytics/message_summary?start=${s}&end=${e}`, wabaAccount)}`);
      if (!respSummary.ok) throw new Error(`Summary: ${respSummary.status}`);
      setSummary(await respSummary.json());
    } catch (err) {
      console.error(err);
    }
  };

  const handleQuickSelect = days => {
    setStartDate(dayjs().subtract(days, 'day'));
    setEndDate(dayjs());
  };

  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Logs');
    XLSX.writeFile(wb, `message_logs_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
  };

  const hiddenCols = isMobile
    ? ['variable_params', 'retries', 'template_id', 'delivered_time', 'read_time']
    : [];

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, background: '#fff' }}>
          <Stack spacing={isMobile ? 2 : 3}>
          <h1 className="text-2xl font-bold text-gray-900">Message Analytics</h1>

            {/* Filters & Buttons */}
            <Stack
              direction={isMobile ? 'column' : 'row'}
              spacing={2}
              justifyContent="space-between"
              alignItems={isMobile ? 'stretch' : 'center'}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: 1
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{ width: isMobile ? '100%' : 'auto', flexWrap: 'wrap' }}
              >
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{
                    textField: {
                      size: 'small',
                      variant: 'outlined',
                      sx: {
                        width: isMobile ? '48%' : 160,
                        '& .MuiOutlinedInput-root': { borderRadius: 2 }
                      }
                    }
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{
                    textField: {
                      size: 'small',
                      variant: 'outlined',
                      sx: {
                        width: isMobile ? '48%' : 160,
                        '& .MuiOutlinedInput-root': { borderRadius: 2 }
                      }
                    }
                  }}
                />
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ width: isMobile ? '100%' : 'auto', flexWrap: 'wrap' }}
                >
                  {[7, 30, 60].map(d => (
                    <Chip
                      key={d}
                      label={`${d} Days`}
                      variant="outlined"
                      onClick={() => handleQuickSelect(d)}
                      sx={{
                        height: 40,
                        borderRadius: 1,
                        borderColor: '#e2e8f0',
                        '&:hover': { bgcolor: '#f1f5f9' },
                        m: isMobile ? '4px 2px' : 0
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
              <Stack
                direction={isMobile ? 'column' : 'row'}
                spacing={2}
                sx={{ width: isMobile ? '100%' : 'auto' }}
              >
                <Button
                  variant="contained"
                  onClick={handleFetch}
                  startIcon={<Refresh sx={{ fontSize: 20 }} />}
                  sx={{ backgroundColor: '#F96168', width: isMobile ? '100%' : 'auto' }}
                >
                  Fetch
                </Button>
                {data.length > 0 && (
                  <Button
                    variant="contained"
                    onClick={handleExportExcel}
                    startIcon={<Download sx={{ fontSize: 20 }} />}
                    sx={{ backgroundColor: '#F96168', width: isMobile ? '100%' : 140 }}
                  >
                    {!isMobile && 'Export'}
                  </Button>
                )}
              </Stack>
            </Stack>

            {/* Summary Cards */}
            {summary && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 4, background: 'linear-gradient(195deg, #f8fafc 0%, #ffffff 100%)' }}>
                <Grid container spacing={2}>
                  {[
                    ['Delivered & Read', summary.delivered_and_read, '#10B981', '✓'],
                    ['Delivered & Not Read', summary.delivered_not_read, '#F96168', '◷'],
                    ['Sent & Not Delivered', summary.sent_not_delivered, '#f59e0b', '↗'],
                    ['Failed', summary.failed, '#ef4444', '⚠'],
                    ['Total', summary.total_messages, '#64748b', '∑'],
                    ['Avg Delivery', `${summary.avg_delivery_time_seconds.toFixed(2)}s`, '#000000', '⏱']
                  ].map(([label, value, color, icon], idx) => (
                    <Grid item xs={12} sm={6} md={4} lg={2} key={idx}>
                      <Box sx={{
                        p: 2,
                        borderRadius: 3,
                        borderLeft: `4px solid ${color}`,
                        background: '#fff',
                        boxShadow: 1
                      }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            bgcolor: `${color}10`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color,
                            fontSize: 18,
                            fontWeight: 600
                          }}>
                            {icon}
                          </Box>
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#64748b',
                                fontWeight: 600,
                                letterSpacing: 0.3,
                                textTransform: 'uppercase'
                              }}
                            >
                              {label}
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5 }}
                            >
                              {value}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}

            {/* Charts & Table */}
            <Box sx={{ width: '100%', mb: 3 }}>
              {trends && (
                <Grid container spacing={isMobile ? 2 : 3} sx={{ overflowX: 'auto' }}>
                  {[
                    { title: 'Delivered & Read',     data: trends.deliveredRead,     color: '#10B981' },
                    { title: 'Delivered & Not Read', data: trends.deliveredNotRead,  color: '#F96168' },
                    { title: 'Sent & Not Delivered', data: trends.sentNotDelivered,  color: '#f59e0b' },
                    { title: 'Failed Messages',      data: trends.failed,            color: '#ef4444' }
                  ].map((m,i) => (
                    <Grid item xs={12} sm={6} md={3} key={i}>
                      <Paper sx={{ p: 2, borderRadius: 3, height: isMobile ? 200 : 300 }}>
                        <Line
                          data={chartDataConfig(trends.labels, m.data, m.color)}
                          options={chartOptions(m.title)}
                        />
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}

              {data.length > 0 && (
                <Box sx={{ overflowX: 'auto' }}>
                  <MaterialReactTable
                    columns={columns}
                    data={data}
                    enableColumnFilters
                    enableSorting
                    enablePagination
                    rowNumberMode="original"
                    initialState={{
                      hiddenColumns: hiddenCols,
                      density: isMobile ? 'compact' : 'comfortable',
                      pagination: { pageSize: isMobile ? 5 : 10, pageIndex: 0 }
                    }}
                    muiTablePaperProps={{
                      sx: { borderRadius: 3, border: '1px solid #f1f5f9' }
                    }}
                    muiTableHeadCellProps={{
                      sx: {
                        bgcolor: '#fff',
                        fontWeight: 700,
                        color: '#0f172a',
                        fontSize: '0.875rem'
                      }
                    }}
                    muiTableBodyCellProps={{
                      sx: {
                        fontSize: '0.875rem',
                        borderBottom: '1px solid #f1f5f9'
                      }
                    }}
                    muiTableBodyRowProps={{
                      sx: {
                        '&:hover': {
                          bgcolor: '#fff',
                          transform: 'scale(1.002)',
                          transition: 'all 0.1s ease'
                        }
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
          </Stack>
        </Container>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
